// In-memory access-token store for non-React callers (e.g. graphqlRequest).
// With WorkOS the access token lives in the httpOnly sealed-session cookie and
// is never readable from JS — the AuthProvider fetches it from
// /api/auth/get-session and pushes it here via setToken().

let currentToken: string | undefined;

export function setToken(token: string | undefined): void {
  currentToken = token;
}

export function getToken(): string | undefined {
  return currentToken;
}

// Lets the AuthProvider refresh its React state after a token refresh.
let refreshCallback: (() => Promise<boolean | void>) | undefined;

export function setRefreshCallback(callback: () => Promise<boolean | void>): void {
  refreshCallback = callback;
}

interface SessionResponse {
  user: unknown;
  accessToken?: string;
}

/**
 * Rotate the WorkOS sealed session and refresh the in-memory access token.
 * Called by graphqlRequest on a 401. Returns true if a fresh token was obtained.
 */
export async function refreshToken(): Promise<boolean> {
  try {
    const refreshed = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (!refreshed.ok) return false;

    // The new access token is delivered by the session endpoint, not a cookie.
    const sessionRes = await fetch("/api/auth/get-session", {
      credentials: "include",
    });
    if (sessionRes.ok) {
      const data = (await sessionRes.json()) as SessionResponse;
      currentToken = data.accessToken;
    }

    if (refreshCallback) await refreshCallback();
    return !!currentToken;
  } catch {
    return false;
  }
}
